import { create } from 'zustand';
import { supabase, authedFetch } from '../lib/supabase';

let authSubscription = null;
let profileRequest = null;
let profileGeneration = 0;

/**
 * Auth store.
 *
 * Standard fields:
 *   - user      : Cognito identity with the backend-mapped application id
 *   - profile   : user_profiles row
 *   - org       : the organization the current screen is operating on
 *                 = profile's home org for therapists / org_admin / super_admin
 *                 = the currently-selected client org for VOMs (see below)
 *   - homeOrg   : the profile's home org (always; never changes per click)
 *
 * VOM client-context switch (Phase 20):
 *   A VOM helps multiple client organizations. Their `profile.org_id` is the
 *   Operon VOM org ("Operon — VOM") which contains no patients/therapists.
 *   To operate on a client they call `setViewingOrg(clientOrgId)`. That:
 *     1. Persists the choice in `localStorage.viewing_org_id` — already read
 *        by `authedFetch()` and sent as the `X-Viewing-Org-Id` header so the
 *        backend resolves into that client's RLS context.
 *     2. Re-loads the `org` field so every screen that reads `useAuth().org`
 *        (Team, Credentialing, Documents, Patients, Schedule, etc.) sees the
 *        client org without per-page workarounds.
 *   `clearViewingOrg()` returns the VOM to their home (no client context).
 *
 *   For non-VOM roles the store ignores `viewingOrg` so the existing behaviour
 *   for therapists / org_admin / super_admin is unchanged. (Super admin has
 *   its own "View as" feature that already writes `viewing_org_id`.)
 */
const VIEWING_KEY = 'viewing_org_id';

function readViewingOrgId() {
  try { return localStorage.getItem(VIEWING_KEY); } catch (_) { return null; }
}

function writeViewingOrgId(orgId) {
  try {
    if (orgId) localStorage.setItem(VIEWING_KEY, orgId);
    else localStorage.removeItem(VIEWING_KEY);
  } catch (_) { /* localStorage unavailable */ }
}

async function fetchOrg(orgId) {
  if (!orgId) return null;
  const { data } = await supabase
    .from('organizations')
    .select('*')
    .eq('id', orgId)
    .single();
  return data || null;
}

export const useAuth = create((set, get) => ({
  user: null,
  profile: null,
  org: null,
  homeOrg: null,
  viewingOrgId: null,
  loading: true,
  initialized: false,

  async init() {
    if (get().initialized) return;
    set({ loading: true });
    if (!authSubscription) {
      authSubscription = supabase.auth.onAuthStateChange((event, sess) => {
        if (event === 'SIGNED_IN' && sess?.user) {
          get().loadProfile(sess.user).catch(() => {
            set({ user: null, profile: null, org: null, homeOrg: null });
          });
        } else if (event === 'SIGNED_OUT') {
          profileGeneration += 1;
          profileRequest = null;
          writeViewingOrgId(null);
          set({ user: null, profile: null, org: null, homeOrg: null, viewingOrgId: null });
        }
      }).data.subscription;
    }
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) await get().loadProfile(session.user);
      else {
        writeViewingOrgId(null);
        set({ user: null, profile: null, org: null, homeOrg: null, viewingOrgId: null });
      }
    } catch (_) {
      set({ user: null, profile: null, org: null, homeOrg: null });
    } finally {
      set({ loading: false, initialized: true });
    }
  },

  async loadProfile(user) {
    if (profileRequest?.sub === user.id) return profileRequest.promise;
    const version = profileGeneration;
    const promise = (async () => {
    // /me verifies Cognito and maps the email to the existing application ID.
    const me = await authedFetch('/me');
    if (version !== profileGeneration) return;
    const { data: profile, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', me.id)
      .single();
    if (error || !profile) throw new Error('Unable to load your practice profile. Please try signing in again.');

    const homeOrg = profile?.org_id ? await fetchOrg(profile.org_id) : null;
    if (version !== profileGeneration) return;

    // For VOMs / super-admins: if a viewing_org_id is persisted, resolve to that.
    let viewingOrgId = readViewingOrgId();
    let activeOrg = homeOrg;
    const canSwitch = profile?.role === 'virtual_ops_manager' || profile?.role === 'super_admin';
    if (canSwitch && viewingOrgId && viewingOrgId !== profile?.org_id) {
      const o = await fetchOrg(viewingOrgId);
      if (o) {
        activeOrg = o;
      } else {
        // stale id — clear it
        viewingOrgId = null;
        writeViewingOrgId(null);
      }
    } else {
      // non-VOM/super-admin shouldn't have a viewing_org_id; clear if stale
      if (!canSwitch && viewingOrgId) {
        viewingOrgId = null;
        writeViewingOrgId(null);
      } else if (canSwitch && !viewingOrgId) {
        viewingOrgId = null;
      }
    }

    if (version === profileGeneration) set({ user: { ...user, sub: user.sub || user.id, id: me.id }, profile, homeOrg, org: activeOrg, viewingOrgId });
    })();
    profileRequest = { sub: user.id, promise };
    try { await promise; } finally { if (profileRequest?.promise === promise) profileRequest = null; }
  },

  /**
   * Switch the active org context for a VOM (or super_admin's "view as").
   * Pass `null` / `undefined` to clear and return to the home org.
   */
  async setViewingOrg(orgId) {
    const { profile } = get();
    const canSwitch = profile?.role === 'virtual_ops_manager' || profile?.role === 'super_admin';
    if (!canSwitch) return;
    if (!orgId) {
      writeViewingOrgId(null);
      set({ viewingOrgId: null, org: get().homeOrg });
      return;
    }
    writeViewingOrgId(orgId);
    const o = await fetchOrg(orgId);
    set({ viewingOrgId: orgId, org: o || get().homeOrg });
  },

  clearViewingOrg() {
    return get().setViewingOrg(null);
  },

  async signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    if (data.challenge) return data;
    await get().loadProfile(data.user);
    return data;
  },

  async signOut() {
    profileGeneration += 1;
    profileRequest = null;
    writeViewingOrgId(null);
    await supabase.auth.signOut();
    set({ user: null, profile: null, org: null, homeOrg: null, viewingOrgId: null });
  },

  isRole(...roles) {
    return roles.includes(get().profile?.role);
  },
}));
